import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface CookiePreferences {
  essential: boolean
  functional: boolean
  analytics: boolean
  marketing: boolean
}

const CookieConsent = () => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be changed
    functional: false,
    analytics: false,
    marketing: false
  })

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent')
    if (!consent) {
      setIsVisible(true)
    }
  }, [])

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted))
    setIsVisible(false)
    
    // Set cookies based on consent
    setCookiesBasedOnPreferences(allAccepted)
  }

  const handleRejectAll = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly))
    setIsVisible(false)
    
    // Set cookies based on consent
    setCookiesBasedOnPreferences(essentialOnly)
  }

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem('cookieConsent', JSON.stringify(consentData))
    setIsVisible(false)
    
    // Set cookies based on consent
    setCookiesBasedOnPreferences(consentData)
  }

  const setCookiesBasedOnPreferences = (prefs: any) => {
    // Store preferences in a cookie for server-side access
    document.cookie = `cookiePreferences=${JSON.stringify(prefs)}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Strict`
    
    // Trigger custom event for other components to respond to
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: prefs }))
  }

  const handlePreferenceChange = (type: keyof CookiePreferences) => {
    if (type === 'essential') return // Cannot change essential cookies
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-orange-100 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t('cookies.title')}
              </h2>
              <p className="text-gray-600">
                {t('cookies.description')}
              </p>
            </div>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-400 hover:text-gray-600 ml-4"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {!showDetails ? (
            <div className="space-y-6">
              <p className="text-gray-700">
                {t('cookies.summary')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-2xl font-semibold hover:from-orange-600 hover:to-amber-600 transition-all duration-300 transform hover:-translate-y-1 shadow-lg"
                >
                  {t('cookies.acceptAll')}
                </button>
                
                <button
                  onClick={handleRejectAll}
                  className="flex-1 border-2 border-orange-200 text-gray-700 px-6 py-3 rounded-2xl font-semibold hover:bg-orange-50 hover:border-orange-300 transition-all duration-300"
                >
                  {t('cookies.rejectAll')}
                </button>
                
                <button
                  onClick={() => setShowDetails(true)}
                  className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                >
                  {t('cookies.customize')}
                </button>
              </div>
              
              <p className="text-sm text-gray-500 text-center">
                {t('cookies.moreInfo')}{' '}
                <Link to="/privacy" className="text-orange-600 hover:text-orange-700 underline">
                  {t('cookies.privacyPolicy')}
                </Link>
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <button
                onClick={() => setShowDetails(false)}
                className="flex items-center text-orange-600 hover:text-orange-700 font-medium"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('cookies.back')}
              </button>
              
              <div className="space-y-6">
                {/* Essential Cookies */}
                <div className="border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {t('cookies.essential.title')}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('cookies.essential.description')}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-2">{t('cookies.alwaysOn')}</span>
                      <div className="bg-green-500 rounded-full w-12 h-6 flex items-center justify-end px-1">
                        <div className="bg-white w-4 h-4 rounded-full shadow"></div>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookies.essential.details')}
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {t('cookies.functional.title')}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('cookies.functional.description')}
                      </p>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange('functional')}
                      className={`rounded-full w-12 h-6 flex items-center px-1 transition-colors ${
                        preferences.functional ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="bg-white w-4 h-4 rounded-full shadow"></div>
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookies.functional.details')}
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {t('cookies.analytics.title')}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('cookies.analytics.description')}
                      </p>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange('analytics')}
                      className={`rounded-full w-12 h-6 flex items-center px-1 transition-colors ${
                        preferences.analytics ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="bg-white w-4 h-4 rounded-full shadow"></div>
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookies.analytics.details')}
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {t('cookies.marketing.title')}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('cookies.marketing.description')}
                      </p>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange('marketing')}
                      className={`rounded-full w-12 h-6 flex items-center px-1 transition-colors ${
                        preferences.marketing ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="bg-white w-4 h-4 rounded-full shadow"></div>
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookies.marketing.details')}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-2xl font-semibold hover:from-orange-600 hover:to-amber-600 transition-all duration-300 transform hover:-translate-y-1 shadow-lg"
                >
                  {t('cookies.savePreferences')}
                </button>
                
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 border-2 border-orange-200 text-gray-700 px-6 py-3 rounded-2xl font-semibold hover:bg-orange-50 hover:border-orange-300 transition-all duration-300"
                >
                  {t('cookies.acceptAll')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CookieConsent